import * as React from "react";
import { I18n } from "react-redux-i18n";

import { FinalNotesPageProps } from "./final-notes-page.types";
import { QuestionnaireFinalNotesPageCreateLocalDto } from "@modules/fhq-design-tool/dto/final-notes-page-create-local-dto";

import { FieldPickerOnChangeValue } from "../../../field-picker";
import { QuestionnaireFinalNotesPageUpdateDto } from "@common/dto/questionnaire/page/final-notes/update-dto";
import { QuestionnaireFieldAddDto } from "@common/dto/questionnaire/field/add-dto";
import { getUniqueFieldCombination } from "@common/utils/fields";
import { QuestionnaireFieldListDto } from "@common/dto/questionnaire/field/list-dto";
import { getFhq4QuestionnaireField } from "@modules/fhq-design-tool/services/questionnaires";

export const useFinalNotesPageController = (props: FinalNotesPageProps) => {
  const { data, onChange, showErrorMessage, cachedData, getPage } = props;
  const [loadingPage, setLoadingPage] = React.useState(false);

  const initializeData = async () => {
    setLoadingPage(true);

    if (!cachedData) {
      try {
        await getPage(data.id);
      } catch (err) {
        console.error(err);
        showErrorMessage(I18n.t("Failed to get page"));
      }
    }

    setLoadingPage(false);
  };

  React.useEffect(() => {
    initializeData();
  }, [data.id]);

  const updatePage = React.useCallback(
    (dataToUpdate: Partial<QuestionnaireFinalNotesPageUpdateDto | QuestionnaireFinalNotesPageCreateLocalDto>) => {
      if (cachedData) {
        onChange({
          ...cachedData,
          ...dataToUpdate,
          modified: true,
        });
      }
    },
    [cachedData, onChange]
  );

  const handlePageNameChange = React.useCallback(
    (val: string) => {
      updatePage({
        title: val.trim(),
      });
    },
    [updatePage]
  );

  const handlePageDescriptionChange = React.useCallback(
    (val: string) => {
      updatePage({
        description: val,
      });
    },
    [updatePage]
  );

  const handleFieldAdd = React.useCallback(
    async (val: FieldPickerOnChangeValue | undefined) => {
      if (!val) {
        return;
      }

      const { field } = val;
      const hasSameField = cachedData?.fields.some((x) =>
        getUniqueFieldCombination({ id: x.progenyId, context: x.context, type: x.progenyFieldType }) ===
        getUniqueFieldCombination({ id: field.id, context: field.context, type: field.type })
      );

      if (hasSameField) {
        return;
      }

      try {
        const fullField = await getFhq4QuestionnaireField({
          id: field.id,
          context: field.context,
          system: field.system,
        });
        
        const newField: QuestionnaireFieldAddDto | QuestionnaireFieldListDto = {
          progenyId: field.id as number,
          name: field.name,
          path: fullField.path || fullField.name,
          order: 1,
          systemField: field.system,
          context: field.context,
          type: fullField.type,
          progenyFieldType: field.type,
          description: fullField.description,
          required: fullField.required,
          defaultValue: fullField.defaultValue,
          allowDuplications: fullField.allowDuplications,
          skipLogic: fullField.skipLogic,
          options: fullField.options,
          subfields: fullField.subfields,
          skipLogicEnabled: fullField.skipLogicEnabled,
          displayInFHQ: fullField.displayInFHQ,
          possibleDropdownOptions: fullField.possibleDropdownOptions,
        };
  
        updatePage({
          fields: [newField as QuestionnaireFieldAddDto],
        });
      } catch (err) {
        console.error(err);
        showErrorMessage(`${I18n.t("Failed to get field with fieldName", { fieldName: field.name })}`);
      }
    },
    [updatePage, cachedData]
  );

  return {
    loadingPage,
    handlePageNameChange,
    handlePageDescriptionChange,
    handleFieldAdd,
  };
};
